import { useMutation, useQuery } from "@tanstack/vue-query";

import { getAdminSamlEnabled, getAdminTotpEnrolment, verifyAdminTotp } from "../api/adminAuth";
import {
  changePassword,
  claimSessionSlot,
  getLoginIdps,
  getSessionSlots,
  getTotpEnrolment,
  revokeSessionSlot,
  verifyTotp,
} from "../api/auth";

export const authKeys = {
  all: ["auth"] as const,
  loginIdps: () => [...authKeys.all, "loginIdps"] as const,
  totpEnrolment: () => [...authKeys.all, "totpEnrolment"] as const,
  sessionSlots: () => [...authKeys.all, "sessionSlots"] as const,
  adminSamlEnabled: () => [...authKeys.all, "admin", "samlEnabled"] as const,
  adminTotpEnrolment: () => [...authKeys.all, "admin", "totpEnrolment"] as const,
};

// A failed read is usually a burst against the shared rate limit.
export const LOGIN_IDPS_RETRY_DELAY_MS = 750;

// A failure leaves no data, which utils/loginIdpFallback.ts reads as unknown.
export function useLoginIdps() {
  return useQuery({
    queryKey: authKeys.loginIdps(),
    queryFn: async ({ signal }) => {
      const list = await getLoginIdps({ signal });
      return Array.isArray(list) ? list : [];
    },
    retry: 1,
    retryDelay: LOGIN_IDPS_RETRY_DELAY_MS,
  });
}

// Every read makes a new TOTP secret, so never refetch or keep it.
export function useTotpEnrolment() {
  return useQuery({
    queryKey: authKeys.totpEnrolment(),
    queryFn: ({ signal }) => getTotpEnrolment({ signal }),
    staleTime: Infinity,
    gcTime: 0,
    refetchOnReconnect: false,
    retry: false,
  });
}

// The TOTP screens read the status themselves before routing on.
export function useVerifyTotp() {
  return useMutation({
    mutationFn: (code: string) => verifyTotp({ Code: code }),
    meta: { invalidates: [], status: false },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: Parameters<typeof changePassword>[0]) => changePassword(body),
    meta: { invalidates: [], status: false },
  });
}

export function useSessionSlots() {
  return useQuery({
    queryKey: authKeys.sessionSlots(),
    queryFn: ({ signal }) => getSessionSlots({ signal }),
    staleTime: 0,
  });
}

// This session still holds no slot, so its status has not changed.
export function useRevokeSessionSlot() {
  return useMutation({
    mutationFn: ({ kind, id }: { kind: string; id: string }) => revokeSessionSlot(kind, id),
    meta: { invalidates: [authKeys.sessionSlots()], status: false },
  });
}

// The route guard on the way to Home reads the status, so the claim waits for
// it to show the session seated.
export function useClaimSessionSlot() {
  return useMutation({
    mutationFn: () => claimSessionSlot(),
    meta: { invalidates: [], status: "await" },
  });
}

// Read on each visit, since an admin who has just set SAML up signs out to try it.
export function useAdminSamlEnabled() {
  return useQuery({
    queryKey: authKeys.adminSamlEnabled(),
    queryFn: ({ signal }) => getAdminSamlEnabled({ signal }),
    staleTime: 0,
    retry: false,
  });
}

// Same caching as useTotpEnrolment: every read makes a new secret.
export function useAdminTotpEnrolment() {
  return useQuery({
    queryKey: authKeys.adminTotpEnrolment(),
    queryFn: ({ signal }) => getAdminTotpEnrolment({ signal }),
    staleTime: Infinity,
    gcTime: 0,
    refetchOnReconnect: false,
    retry: false,
  });
}

// The admin TOTP screens read the status themselves before routing on.
export function useVerifyAdminTotp() {
  return useMutation({
    mutationFn: (body: Parameters<typeof verifyAdminTotp>[0]) => verifyAdminTotp(body),
    meta: { invalidates: [], status: false },
  });
}
